// Pack walk planning hooks: paginated chat (light 5s polling while mounted)
// + send/vote/close mutations (surgical cache updates, no full invalidation).
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import { subscribeChatEvents, isWsConnected } from '@/lib/ws';
import {
  fetchGroupMessages, postGroupMessage, deleteGroupMessage, editGroupMessage,
  voteGroupPoll, closeGroupPoll,
  type GroupChatMessage, type GroupPollDto,
} from '@/lib/api/groupChat';

const CHAT_POLL_MS = 5000; // socket down: full polling
const CHAT_POLL_MS_FALLBACK = 60000; // socket live: light safety net
export const groupChatKey = (groupId: string) => ['group', 'chat', groupId] as const;

type ChatPage = GroupChatMessage[];

export function useGroupMessages(token: string | undefined, groupId: string | undefined) {
  return useInfiniteQuery<ChatPage, Error>({
    queryKey: groupChatKey(groupId ?? 'none'),
    initialPageParam: null as [string, string] | null,
    queryFn: async ({ pageParam }) => {
      const cursor = pageParam as [string, string] | null | undefined;
      // cursor = [createdAt, seqString] of the last loaded row; seq is a
      // STRING on the wire — unique_rowid values exceed JS safe-integer range
      // and rounding them as numbers corrupted the keyset cursor (looping).
      return fetchGroupMessages(token!, groupId!, {
        limit: 50,
        beforeCreated: cursor?.[0],
        beforeSeq: cursor?.[1],
      });
    },
    getNextPageParam: (lastPage) => {
      if (lastPage.length === 0) return undefined;
      const last = lastPage[lastPage.length - 1];
      if (last.seq == null || !last.createdAt) return undefined;
      return [last.createdAt, last.seq];
    },
    enabled: !!token && !!groupId,
    // live updates arrive via the websocket; polling is the fallback
    refetchInterval: (query) => (isWsConnected() ? CHAT_POLL_MS_FALLBACK : CHAT_POLL_MS),
    refetchIntervalInBackground: false,
  });
}

/** Apply websocket events to the chat cache surgically (no refetches). */
export function useChatWsEvents(token: string | undefined, groupId: string | undefined) {
  const qc = useQueryClient();
  React.useEffect(() => {
    if (!groupId) return;
    const key = groupChatKey(groupId);
    return subscribeChatEvents((e) => {
      if (e.group_id !== groupId) return;
      qc.setQueriesData<{ pages: GroupChatMessage[][] }>(
        { queryKey: key },
        (old) => {
          if (!old) return old;
          let pages = old.pages;
          if (e.event === 'message.new') {
            // prepend to the newest page (dedupe if already present)
            const [first, ...rest] = pages;
            if (!first) return old;
            if (first.some((m) => m.id === e.message.id)) return old;
            pages = [[e.message, ...first], ...rest];
          } else if (e.event === 'message.deleted') {
            pages = pages.map((p) => p.filter((m) => m.id !== e.message_id));
          } else if (e.event === 'message.edited') {
            pages = pages.map((p) => p.map((m) => (m.id === e.message.id ? e.message : m)));
          } else if (e.event === 'poll.voted' || e.event === 'poll.closed') {
            pages = pages.map((p) => p.map((m) => (m.poll && m.poll.id === e.poll.id ? { ...m, poll: e.poll } : m)));
          }
          return { ...old, pages };
        },
      );
    });
  }, [qc, groupId]);
}

/** Flatten pages newest→oldest with duplicates dropped (same row on refetches). */
export function flattenChatPages(data: { pages: ChatPage[] } | undefined): GroupChatMessage[] {
  if (!data) return [];
  const seen = new Set<string>();
  const out: GroupChatMessage[] = [];
  for (const page of data.pages) {
    for (const msg of page) {
      if (!seen.has(msg.id)) {
        seen.add(msg.id);
        out.push(msg);
      }
    }
  }
  return out;
}

export function useSendGroupMessage(token: string | undefined, groupId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (message: { type: string; body?: string | null; payload?: Record<string, any> }) => {
      if (!token || !groupId) throw new Error('No session');
      return postGroupMessage(token, groupId, message);
    },
    onSuccess: () => {
      // newest page refetches immediately; poll interval continues after
      qc.invalidateQueries({ queryKey: groupChatKey(groupId ?? 'none') });
    },
  });
}

/** Inline-upsert the poll DTO inside every cached chat page (surgical). */
export function useUpdatePollInCache(token: string | undefined, groupId: string | undefined) {
  const qc = useQueryClient();
  return useCallback(
    (poll: GroupPollDto) => {
      qc.setQueriesData<{ pages: ChatPage[] }>(
        { queryKey: groupChatKey(groupId ?? 'none') },
        (old) =>
          old
            ? {
                ...old,
                pages: old.pages.map((page) =>
                  page.map((m) => (m.poll && m.poll.id === poll.id ? { ...m, poll } : m)),
                ),
              }
            : old,
      );
    },
    [qc, groupId],
  );
}

export function useVoteGroupPoll(token: string | undefined, groupId: string | undefined) {
  const updatePollInCache = useUpdatePollInCache(token, groupId);
  return useMutation({
    mutationFn: ({ pollId, optionIndex }: { pollId: string; optionIndex: number }) => {
      if (!token || !groupId) throw new Error('No session');
      return voteGroupPoll(token, groupId, pollId, optionIndex);
    },
    onSuccess: (poll) => updatePollInCache(poll),
  });
}

export function useCloseGroupPoll(token: string | undefined, groupId: string | undefined) {
  const updatePollInCache = useUpdatePollInCache(token, groupId);
  return useMutation({
    mutationFn: ({ pollId }: { pollId: string }) => {
      if (!token || !groupId) throw new Error('No session');
      return closeGroupPoll(token, groupId, pollId);
    },
    onSuccess: (poll) => updatePollInCache(poll),
  });
}

export function useEditGroupMessage(token: string | undefined, groupId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ messageId, body, options }: { messageId: string; body: string; options?: string[] }) => {
      if (!token || !groupId) throw new Error('No session');
      return editGroupMessage(token, groupId, messageId, body, options);
    },
    onSuccess: (updated) => {
      // surgical: replace the edited message in every cached chat page
      qc.setQueriesData<{ pages: GroupChatMessage[][] }>(
        { queryKey: groupChatKey(groupId ?? 'none') },
        (old) =>
          old
            ? { ...old, pages: old.pages.map((page) => page.map((m) => (m.id === updated.id ? updated : m))) }
            : old,
      );
    },
  });
}

export function useDeleteGroupMessage(token: string | undefined, groupId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (messageId: string) => {
      if (!token || !groupId) throw new Error('No session');
      return deleteGroupMessage(token, groupId, messageId);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: groupChatKey(groupId ?? 'none') });
    },
  });
}